import React from "react";
import { Link } from "react-router-dom";
import { Grid, Box, Typography } from "@material-ui/core";
import HomeWorkIcon from "@material-ui/icons/HomeWork";
import PersonIcon from "@material-ui/icons/Person";
import AccountBalanceIcon from "@material-ui/icons/AccountBalance";
import HttpService from "../services/httpService";
import BusinessCenterIcon from "@material-ui/icons/BusinessCenter";
import AssignmentIndIcon from "@material-ui/icons/AssignmentInd";

// ─────────────────────────────────────────────────────────────────────────
// Single source of truth for every approval type that lives OUTSIDE the
// native MiscPayment collection (rent/housekeeping/EC payments etc.) but
// still needs a "pending my approval" queue somewhere in the ERP. Consumed
// by both the standalone ApprovalCenter page and the "Other Payment Type"
// view embedded in the Misc Payment dashboard — one registry, two homes.
//
// To add a new approval type later:
//   1. Add fetch/approve HttpService methods for it (mirroring the site-visit ones).
//   2. Append an entry below with a `key`, `label`, `icon`, `fetchApprovals`,
//      `approveAction`, `rowKey`, `approveId`, `title`, `amount`, and
//      `renderCardBody(row, classes)` returning the card's info-grid content.
// Nothing in the consuming components needs to change.
// ─────────────────────────────────────────────────────────────────────────

export const InfoItem = ({ icon, label, linkTo }) => {
    const content = (
        <Typography variant="body2" noWrap>
            {label || "-"}
        </Typography>
    );
    return (
        <Grid item xs={12} sm={6} md={3}>
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                {icon}
                {linkTo ? (
                    <Link to={linkTo} style={{ color: "#2563eb", textDecoration: "none" }}>
                        {content}
                    </Link>
                ) : content}
            </Box>
        </Grid>
    );
};
export const DetailItem = ({ icon, label, value, node, linkTo, md = 3 }) => {
    const valueContent = (
        <Typography
            variant="body2"
            style={{ fontWeight: 600, wordBreak: "break-word", color: "#1a2233", lineHeight: 1.35 }}
        >
            {node || value || "-"}
        </Typography>
    );
    return (
        <Grid item xs={12} sm={6} md={md}>
            <Box
                display="flex"
                alignItems="flex-start"
                style={{
                    gap: 8,
                    height: "100%",
                    boxSizing: "border-box",
                    backgroundColor: "#f8f9fc",
                    border: "1px solid #edf0f7",
                    borderRadius: 10,
                    padding: "10px 12px",
                }}
            >
                {icon && <Box style={{ marginTop: 2, color: "#8b95a8", display: "flex" }}>{icon}</Box>}
                <Box style={{ minWidth: 0 }}>
                    <Typography
                        variant="caption"
                        style={{
                            display: "block",
                            lineHeight: 1.4,
                            color: "#8b95a8",
                            textTransform: "uppercase",
                            fontSize: "0.68rem",
                            letterSpacing: 0.4,
                            fontWeight: 600,
                        }}
                    >
                        {label}
                    </Typography>
                    {linkTo ? (
                        <Link to={linkTo} style={{ color: "#2563eb", textDecoration: "none" }}>
                            {valueContent}
                        </Link>
                    ) : valueContent}
                </Box>
            </Box>
        </Grid>
    );
};

export const APPROVAL_TYPES = [
    {
        key: "siteVisit",
        label: "Physical Site Visit",
        icon: <HomeWorkIcon fontSize="small" />,
        fetchApprovals: () => HttpService.getSiteVisitPaymentApprovals(),
        fetchHistory: (params) => HttpService.getSiteVisitPaymentHistory(params),
        approveAction: (id, data) => HttpService.approveSiteVisitPayment(id, data),
        rowKey: (row) => row._id,
        approveId: (row) => row?.leadId?._id,
        title: (row) => row?.referrerLeadId?.customerId?.name || "Physical Site Visit",
        amount: (row) => row?.amount,
        renderCardBody: (row, classes) => (
            <Grid container spacing={2}>
                <InfoItem
                    icon={<PersonIcon fontSize="small" color="action" />}
                    label={`Prospect: ${row?.leadId?.customerId?.name || "-"} (${row?.leadId?.lead_no || row?.leadId?.uniqueLeadNumber || "-"})`}
                    linkTo={row?.leadId?._id ? `/project-logs/${row.leadId._id}` : null}
                />
                <InfoItem
                    icon={<PersonIcon fontSize="small" color="action" />}
                    label={`Referrer: ${row?.referrerLeadId?.customerId?.name || "-"} (${row?.referrerLeadId?.uniqueLeadNumber || "-"})`}
                    linkTo={row?.referrerLeadId?._id ? `/project-logs/${row.referrerLeadId._id}` : null}
                />
                <InfoItem
                    icon={<PersonIcon fontSize="small" color="action" />}
                    label={`Raised by: ${row?.raisedBy?.name || "Unknown"}`}
                />
                <InfoItem
                    icon={<AccountBalanceIcon fontSize="small" color="action" />}
                    label={`Bank Details: ${row?.bankDetailsSubmitted ? "Submitted" : "Pending"}`}
                />
            </Grid>
        ),
    },
    {
        key: "channelPartnerPayout",
        label: "Channel Partner",
        icon: <BusinessCenterIcon fontSize="small" />,
        fetchApprovals: () => HttpService.getAllChannelPartnerPayments({ status: "pending_verification" }),
        fetchHistory: () => HttpService.getAllChannelPartnerPayments(),
        approveAction: (id, data) => HttpService.actOnChannelPartnerPayout(id, data),
        rowKey: (row) => row._id,
        approveId: (row) => row._id,
        title: (row) => row?.channelPartnerId?.full_name || "Channel Partner Payout",
        amount: (row) => row?.amount,
        renderCardBody: (row) => (
            <Grid container spacing={2}>
                <InfoItem
                    icon={<AssignmentIndIcon fontSize="small" color="action" />}
                    label={`Channel Partner: ${row?.channelPartnerId?.full_name || "-"}`}
                />
                <InfoItem
                    icon={<PersonIcon fontSize="small" color="action" />}
                    label={`Lead: ${row?.leadId?.customerId?.name || "-"} (${row?.uniqueLeadNumber || row?.lead_no || "-"})`}
                    linkTo={row?.leadId?._id ? `/project-logs/${row.leadId._id}` : null}
                />
                <InfoItem
                    icon={<PersonIcon fontSize="small" color="action" />}
                    label={`Raised by: ${row?.raised_by?.name || "Unknown"}`}
                />
                <InfoItem
                    icon={<AccountBalanceIcon fontSize="small" color="action" />}
                    label={`${row?.payout_percentage || 5}% of quotation subtotal ₹${Number(row?.subTotal || 0).toLocaleString()}`}
                />
            </Grid>
        ),
        renderDetails: (row) => (
            <Grid container spacing={3}>
                <DetailItem
                    icon={<AssignmentIndIcon fontSize="small" color="action" />}
                    label="Channel Partner"
                    value={row?.channelPartnerId?.full_name}
                />
                <DetailItem
                    icon={<PersonIcon fontSize="small" color="action" />}
                    label="Customer"
                    value={row?.leadId?.customerId?.name}
                    linkTo={row?.leadId?._id ? `/project-logs/${row.leadId._id}` : null}
                />
                <DetailItem
                    icon={<PersonIcon fontSize="small" color="action" />}
                    label="Lead No"
                    value={row?.lead_no || row?.uniqueLeadNumber}
                />
                <DetailItem
                    icon={<AccountBalanceIcon fontSize="small" color="action" />}
                    label="Quotation Subtotal"
                    value={`₹${Number(row?.subTotal || 0).toLocaleString()}`}
                />
                <DetailItem
                    icon={<AccountBalanceIcon fontSize="small" color="action" />}
                    label="Payout %"
                    value={`${row?.payout_percentage || 5}%`}
                />
                <DetailItem
                    icon={<AccountBalanceIcon fontSize="small" color="action" />}
                    label="Amount"
                    value={`₹${Number(row?.amount || 0).toLocaleString()}`}
                />
                <DetailItem
                    icon={<PersonIcon fontSize="small" color="action" />}
                    label="Raised by"
                    value={row?.raised_by?.name || "Unknown"}
                />
                {row?.verified_by && (
                    <DetailItem
                        icon={<PersonIcon fontSize="small" color="action" />}
                        label={row?.status === "rejected" ? "Rejected by" : "Verified by"}
                        value={row.verified_by?.name}
                    />
                )}
                {row?.remarks && (
                    <DetailItem icon={<PersonIcon fontSize="small" color="action" />} label="Remarks" value={row.remarks} md={12} />
                )}
            </Grid>
        ),
    },
    // ── Future approval types get appended here, e.g.:
    // { key: "xyz", label: "XYZ Approval", icon: <SomeIcon fontSize="small"/>, fetchApprovals, approveAction, rowKey, approveId, title, amount, renderCardBody },
];
